import React, { useEffect, useMemo, useState } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import MenuIcon from '@mui/icons-material/Menu';
import {
  AppBar,
  BottomNavigation,
  BottomNavigationAction,
  Box,
  Divider,
  Drawer,
  Grid,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Toolbar,
  Typography,
} from '@mui/material';

import Button from 'components/Button';
import { mapSidebarMarketplace } from 'components/Helper/Filter/mappings/sidebarMarketplace';
import ConfirmLogoutModal from 'components/Logout/components/ConfirmLogoutModal';
import { useMenu } from 'global/MenuContext';
import { useMenuSelection } from 'global/MenuSelectionContext';
import { useUser } from 'global/UserContext';
import { useMenuTitle } from 'hooks/useMenuTitleContext';
import { useSolutionsMenu } from 'hooks/useSolutionsMenu';
import { Icon, MeetingRoomExtras, MenuItem, isMeetingRoomExtras } from 'types/menuTypes';

const COCKPIT_DOCUMENT_ID = 'bhnrs7gefue5v5ntzqg3ls74';
const COCKPIT_AGENDA_DOCUMENT_ID = 'eh04q5jlhm52tnh1tthycrkd';

const StyledListItemButton = styled(ListItemButton)`
  &.Mui-selected {
    background-color: ${props => props.theme.palette.primary.main};
    color: ${props => props.theme.palette.primary.contrastText};
    &:hover {
      background-color: ${props => props.theme.palette.primary.dark};
    }
  }
`;

const StyledListItemIcon = styled(ListItemIcon)`
  color: inherit;
  min-width: 40px;
`;

// Função utilitária para renderizar ícone de menu
const renderMenuIcon = (icon: any, alt: string, size = 24) => {
  if (Array.isArray(icon) && icon[0]?.url) {
    return <img src={icon[0].url} alt={alt} style={{ width: size, height: size }} />;
  }
  if (icon && typeof icon === 'object' && icon.url) {
    return <img src={icon.url} alt={alt} style={{ width: size, height: size }} />;
  }
  return <MenuIcon sx={{ fontSize: size }} />;
};

// Busca o ícone do menu ou do pai mais próximo na hierarquia
const findMenuIcon = (menuItem: MenuItem, menuList: MenuItem[]): MenuItem['icon'] | null => {
  if (
    menuItem.icon &&
    (Array.isArray(menuItem.icon) ? menuItem.icon.length : (menuItem.icon as any).url)
  ) {
    return menuItem.icon;
  }
  for (const parent of menuList) {
    if (
      parent.subMenu?.some(
        (sub: MenuItem) => sub.code === menuItem.code || sub.documentId === menuItem.documentId
      )
    ) {
      return parent.icon;
    }
    if (parent.subMenu?.length) {
      const found = findMenuIcon(menuItem, parent.subMenu);
      if (found) return found;
    }
  }
  return null;
};

const MobileMenu: React.FC = () => {
  const { menu } = useMenu();
  const { updateMenuTitle } = useMenuTitle();
  const navigate = useNavigate();
  const location = useLocation();
  const [navValue, setNavValue] = useState('menu');
  const [openMenu, setOpenMenu] = useState(false);
  const [selectedSecond, setSelectedSecond] = useState<MenuItem | null>(null);
  const [selectedFirstForThird, setSelectedFirstForThird] = useState<MenuItem | null>(null);
  const { logout } = useUser();
  const { sideMenu: sideMenuFromMenu } = useMenu();
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);
  const { activeSolutions, hasActiveSolutions } = useSolutionsMenu();
  const {
    currentLevel,
    firstLevelMenu,
    secondLevelMenu,
    thirdLevelMenu,
    activeModulePath,
    activeDocumentId,
    selectMenuByDocumentId,
    selectMenuByPath,
  } = useMenuSelection();
  const [selectedMarketplaceItem, setSelectedMarketplaceItem] = useState<string | null>(null);
  const sideMenu = sideMenuFromMenu; // Use the one from MenuContext
  const [openMenus, setOpenMenus] = useState<{ [key: string]: boolean }>({});
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState<MenuItem | null>(null);

  // Função para encontrar o primeiro menu válido (busca recursiva)
  const findFirstValidMenu = (menuItems: MenuItem[]): MenuItem | null => {
    for (const menuItem of menuItems) {
      if (menuItem.isActive) {
        if (menuItem.subMenu?.length) {
          const found = findFirstValidMenu(menuItem.subMenu);
          if (found) return found;
        }
        return menuItem;
      }
    }
    return null;
  };

  const firstValidMenu = findFirstValidMenu(menu);

  useEffect(() => {
    updateMenuTitle(firstValidMenu?.title || 'Menu');
  }, [firstValidMenu, updateMenuTitle]);

  // Memoize os menus para evitar recálculos desnecessários
  const {
    firstLevelMenus,
    groupedFirstLevelMenus,
    singleFirstLevelMenus,
    initialMenu,
    cockpitMenu,
    cockpitAgendaMenu,
    solutionsMenu,
  } = useMemo(() => {
    const firstLevelMenus: MenuItem[] = menu.filter(
      (item: MenuItem) => item.level === 'first' && item.isActive
    );

    // Busca pelos menus específicos do Cockpit
    const cockpitMenu = firstLevelMenus.find(item => item.documentId === COCKPIT_DOCUMENT_ID);
    const cockpitAgendaMenu = firstLevelMenus.find(
      item => item.documentId === COCKPIT_AGENDA_DOCUMENT_ID
    );
    // Busca pelo menu de soluções
    const solutionsMenu = firstLevelMenus.find(item => item.label?.toLowerCase().includes('soluç'));

    // Prioriza Cockpit Agenda sobre Cockpit
    const initialMenu = cockpitAgendaMenu || cockpitMenu;

    // Menus com submenus (grupos)
    const groupedFirstLevelMenus = firstLevelMenus.filter(
      item => Array.isArray(item.subMenu) && item.subMenu.length > 0
    );

    // Menus sem submenus (itens simples)
    const singleFirstLevelMenus = firstLevelMenus.filter(
      item => !item.subMenu || item.subMenu.length === 0
    );

    return {
      firstLevelMenus,
      groupedFirstLevelMenus,
      singleFirstLevelMenus,
      initialMenu,
      cockpitMenu,
      cockpitAgendaMenu,
      solutionsMenu,
    };
  }, [menu]);

  // Novo: menus de soluções filtrados por path
  const solutionsMenusList = useMemo(() => {
    return firstLevelMenus.filter(
      item =>
        item.path &&
        (item.path.startsWith('/solucoes') || item.path.startsWith('/solutions')) &&
        item.isActive
    );
  }, [firstLevelMenus]);

  // Novo: submenus do menu de soluções
  const solutionsSubMenus = useMemo(() => {
    const solutionsMenuItem = firstLevelMenus.find(
      item =>
        item.label && item.label.toLowerCase().includes('soluç') && item.subMenu && item.isActive
    );
    return solutionsMenuItem?.subMenu?.filter(sub => sub.isActive) || [];
  }, [firstLevelMenus]);

  // Sincroniza navValue com a rota atual para garantir highlight correto
  useEffect(() => {
    if (initialMenu && location.pathname === initialMenu.path) {
      setNavValue('initial');
    } else if (location.pathname === '/menu') {
      setNavValue('menu');
    } else if (location.pathname.startsWith('/solutions')) {
      setNavValue('solutions');
    }
    // Adapte para outros casos se necessário
  }, [location.pathname, initialMenu]);

  const marketplaceItems = useMemo(() => mapSidebarMarketplace(sideMenu), [sideMenu]);

  // Efeito para abrir o menu ativo quando o nível mudar
  useEffect(() => {
    if (currentLevel === 'first' && firstLevelMenu) {
      setOpenMenus(prev => ({ ...prev, [firstLevelMenu.documentId || '']: true }));
    } else if (currentLevel === 'second' && secondLevelMenu) {
      setOpenMenus(prev => ({ ...prev, [secondLevelMenu.documentId || '']: true }));
    } else if (currentLevel === 'third' && thirdLevelMenu) {
      setOpenMenus(prev => ({ ...prev, [thirdLevelMenu.documentId || '']: true }));
    }
  }, [currentLevel, firstLevelMenu, secondLevelMenu, thirdLevelMenu]);

  // Handlers
  const handleNavChange = (_: any, newValue: string) => {
    setNavValue(newValue);
    if (newValue === 'menu' || newValue === 'solutions') {
      setOpenMenu(true);
      setSelectedSecond(null);
      setSelectedFirstForThird(null);
    } else if (newValue === 'initial' && initialMenu) {
      selectMenuByPath(initialMenu.path);
      navigate(initialMenu.path);
    }
  };

  const handleFirstLevelClick = (item: MenuItem) => {
    if (item.subMenu && item.subMenu.length > 0) {
      setSelectedSecond(item);
      setNavValue('submenu');
      setDrawerOpen(false);
      return;
    }
    if (item.feature?.documentId) {
      selectMenuByDocumentId(item.feature.documentId, true, undefined, item.path);
      navigate(item.path);
    } else if (
      isMeetingRoomExtras(item.extras ?? null) &&
      typeof (item.extras as MeetingRoomExtras).internal_id === 'string'
    ) {
      selectMenuByDocumentId(
        (item.extras as MeetingRoomExtras).internal_id!,
        true,
        undefined,
        item.path
      );
      navigate(item.path);
    }
    setOpenMenu(false);
    setDrawerOpen(false);
  };

  const handleSecondLevelClick = (item: MenuItem, parent: MenuItem) => {
    if (item.subMenu && item.subMenu.length > 0) {
      const firstThird = item.subMenu[0];
      if (firstThird.feature?.documentId) {
        selectMenuByDocumentId(firstThird.feature.documentId, true, undefined, firstThird.path);
        navigate(firstThird.path);
      } else if (
        isMeetingRoomExtras(firstThird.extras ?? null) &&
        typeof (firstThird.extras as MeetingRoomExtras).internal_id === 'string'
      ) {
        selectMenuByDocumentId(
          (firstThird.extras as MeetingRoomExtras).internal_id!,
          true,
          undefined,
          firstThird.path
        );
        navigate(firstThird.path);
      }
      setOpenMenu(false);
      setDrawerOpen(false);
      return;
    }
    if (
      item.feature?.dynamic_component === 'MEETING_ROOM' &&
      item.subMenu &&
      item.subMenu.length > 0
    ) {
      const firstSubMenu = item.subMenu[0];
      if (firstSubMenu) {
        if (firstSubMenu.feature?.documentId) {
          selectMenuByDocumentId(
            firstSubMenu.feature.documentId,
            true,
            undefined,
            firstSubMenu.path
          );
          navigate(firstSubMenu.path);
        } else if (
          isMeetingRoomExtras(firstSubMenu.extras ?? null) &&
          typeof (firstSubMenu.extras as MeetingRoomExtras).internal_id === 'string'
        ) {
          selectMenuByDocumentId(
            (firstSubMenu.extras as MeetingRoomExtras).internal_id!,
            true,
            undefined,
            firstSubMenu.path
          );
          navigate(firstSubMenu.path);
        }
        setOpenMenu(false);
        setDrawerOpen(false);
        return;
      }
    }
    if (item.feature?.documentId) {
      selectMenuByDocumentId(item.feature.documentId, true, undefined, item.path);
      navigate(item.path);
    } else if (
      isMeetingRoomExtras(item.extras ?? null) &&
      typeof (item.extras as MeetingRoomExtras).internal_id === 'string'
    ) {
      selectMenuByDocumentId(
        (item.extras as MeetingRoomExtras).internal_id!,
        true,
        undefined,
        item.path
      );
      navigate(item.path);
    }
    setOpenMenu(false);
    setDrawerOpen(false);
  };

  const handleThirdLevelClick = (
    item: MenuItem,
    parent: MenuItem | null,
    parentSecondLevel: MenuItem | null
  ) => {
    if (item.feature?.documentId) {
      selectMenuByDocumentId(item.feature.documentId, true, undefined, item.path);
      navigate(item.path);
    } else if (
      isMeetingRoomExtras(item.extras ?? null) &&
      typeof (item.extras as MeetingRoomExtras).internal_id === 'string'
    ) {
      selectMenuByDocumentId(
        (item.extras as MeetingRoomExtras).internal_id!,
        true,
        undefined,
        item.path
      );
      navigate(item.path);
    }
    setOpenMenu(false);
    setDrawerOpen(false);
  };

  const handleBackToSecond = () => {
    setSelectedSecond(null);
    setSelectedFirstForThird(null);
  };

  const handleOpenLogoutModal = () => setLogoutModalOpen(true);
  const handleCloseLogoutModal = () => setLogoutModalOpen(false);
  const handleConfirmLogout = () => {
    setLogoutModalOpen(false);
    setTimeout(() => {
      logout();
    }, 300);
  };

  const handleMarketplaceItemClick = (type: string) => {
    const routeMap: Record<string, { path: string; title: string }> = {
      advisor: { path: '/marketplace/assessores', title: 'Assessores' },
      courses: { path: '/marketplace/cursos', title: 'Cursos' },
      services: { path: '/marketplace/servicos', title: 'Serviços' },
      plans: { path: '/marketplace/planos', title: 'Planos' },
    };

    const route = routeMap[type];
    if (route) {
      updateMenuTitle(route.title);
      navigate(route.path);
      setOpenMenu(false);
    }
  };

  const handleClick = (item: MenuItem) => {
    setOpenMenus(prev => ({ ...prev, [item.documentId || '']: !prev[item.documentId || ''] }));
  };

  const handleItemClick = (item: MenuItem) => {
    selectMenuByPath(item.path || '/');
    navigate(item.path || '/');
    setDrawerOpen(false);
  };

  const handleMenuClick = (item: MenuItem) => {
    if (item.subMenu && item.subMenu.length > 0) {
      setSelectedMenu(item);
      setDrawerOpen(true);
    } else {
      handleItemClick(item);
    }
  };

  // Função para renderizar ícone, seja string, objeto ou array
  const renderIcon = (icon: Icon | Icon[] | string | undefined, label: string) => {
    if (!icon) return null;
    if (typeof icon === 'string') {
      return <img src={icon} alt={label} style={{ width: 24, height: 24 }} />;
    }
    if (Array.isArray(icon)) {
      if (icon.length > 0 && icon[0].url) {
        return (
          <img src={icon[0].url} alt={icon[0].name || label} style={{ width: 24, height: 24 }} />
        );
      }
      return null;
    }
    if (typeof icon === 'object' && icon.url) {
      return <img src={icon.url} alt={icon.name || label} style={{ width: 24, height: 24 }} />;
    }
    return null;
  };

  const renderMenuItem = (item: MenuItem, level: 'first' | 'second' | 'third' = 'first') => {
    const hasChildren = item.subMenu && item.subMenu.length > 0;
    const isOpen = openMenus[item.documentId || ''] || false;
    const isSelected = item.documentId === activeDocumentId;

    return (
      <React.Fragment key={item.documentId}>
        <StyledListItemButton
          selected={isSelected}
          onClick={() => (hasChildren ? handleClick(item) : handleItemClick(item))}
          sx={{
            pl: level === 'first' ? 2 : level === 'second' ? 4 : 6,
            py: 1,
          }}
        >
          {item.icon && (
            <StyledListItemIcon>{renderIcon(item.icon, item.label)}</StyledListItemIcon>
          )}
          <ListItemText
            primary={
              <Typography variant="body2" sx={{ fontWeight: isSelected ? 'bold' : 'normal' }}>
                {item.label}
              </Typography>
            }
          />
        </StyledListItemButton>
      </React.Fragment>
    );
  };

  const renderMenuGroups = () => (
    <Box p={2}>
      {/* Cockpit/Cockpit Agenda destacado no topo, igual ao protótipo */}
      {(cockpitAgendaMenu || cockpitMenu) && (
        <Box mb={3}>
          <Box display="flex" alignItems="center" mb={1} gap={1}>
            {renderMenuIcon(
              (cockpitAgendaMenu || cockpitMenu)?.icon,
              (cockpitAgendaMenu || cockpitMenu)?.label || ''
            )}
            <Typography variant="subtitle1">
              {(cockpitAgendaMenu || cockpitMenu)?.label || ''}
            </Typography>
            <Divider flexItem sx={{ flex: 1, alignSelf: 'center' }} />
          </Box>
          <Paper
            elevation={3}
            sx={{
              p: 2,
              textAlign: 'center',
              cursor: 'pointer',
              mb: 1,
              width: '48%',
              bgcolor: 'background.default',
            }}
            onClick={() => {
              const menuItem = cockpitAgendaMenu || cockpitMenu;
              if (menuItem) handleFirstLevelClick(menuItem);
            }}
          >
            {renderMenuIcon(
              (cockpitAgendaMenu || cockpitMenu)?.icon,
              (cockpitAgendaMenu || cockpitMenu)?.label || ''
            )}
            <Typography variant="subtitle1">
              {(cockpitAgendaMenu || cockpitMenu)?.label || ''}
            </Typography>
          </Paper>
        </Box>
      )}
      {/* Menus simples de primeiro nível (sem submenus), exceto Cockpit/Cockpit Agenda */}
      {singleFirstLevelMenus
        .filter(item => item !== cockpitMenu && item !== cockpitAgendaMenu)
        .map(item => (
          <Box
            key={
              item.feature?.documentId ||
              (isMeetingRoomExtras(item.extras ?? null) &&
              typeof (item.extras as MeetingRoomExtras).internal_id === 'string'
                ? (item.extras as MeetingRoomExtras).internal_id!
                : item.code)
            }
            mb={2}
          >
            <Divider sx={{ mb: 2 }} />
            <Paper
              elevation={3}
              sx={{
                p: 2,
                textAlign: 'center',
                cursor: 'pointer',
                width: '100%',
                bgcolor: 'background.default',
              }}
              onClick={() => handleFirstLevelClick(item)}
            >
              {renderMenuIcon(item.icon, item.label || '')}
              <Typography variant="subtitle1">{item.label || ''}</Typography>
            </Paper>
          </Box>
        ))}
      {/* Grupos de primeiro nível (com submenus) */}
      {groupedFirstLevelMenus.map(group => (
        <Box
          key={
            group.feature?.documentId ||
            (isMeetingRoomExtras(group.extras ?? null) &&
            typeof (group.extras as MeetingRoomExtras).internal_id === 'string'
              ? (group.extras as MeetingRoomExtras).internal_id!
              : group.code)
          }
          mb={4}
        >
          <Stack direction="row" alignItems="center" spacing={1} mb={1}>
            {renderMenuIcon(group.icon, group.label || '')}
            <Typography variant="subtitle1">{group.label || ''}</Typography>
            <Divider flexItem sx={{ flex: 1, alignSelf: 'center' }} />
          </Stack>
          <Grid container spacing={2}>
            {(group.subMenu || []).map(item => (
              <Grid
                item
                xs={6}
                key={
                  item.feature?.documentId ||
                  (isMeetingRoomExtras(item.extras ?? null) &&
                  typeof (item.extras as MeetingRoomExtras).internal_id === 'string'
                    ? (item.extras as MeetingRoomExtras).internal_id!
                    : item.code)
                }
              >
                <Paper
                  elevation={2}
                  sx={{
                    p: 2,
                    textAlign: 'center',
                    cursor: 'pointer',
                    bgcolor: 'background.default',
                  }}
                  onClick={() => handleSecondLevelClick(item, group)}
                >
                  {renderMenuIcon(item.icon, item.label || '', 28)}
                  <Typography variant="body2">{item.label || ''}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
      ))}
    </Box>
  );

  const renderThirdLevel = () => (
    <Box>
      <Stack direction="column" alignItems="center" spacing={1}>
        <AppBar
          position="static"
          color="default"
          elevation={1}
          sx={{ borderTopLeftRadius: 16, borderTopRightRadius: 16 }}
        >
          <Toolbar>
            <IconButton edge="start" color="inherit" onClick={handleBackToSecond} aria-label="back">
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              {selectedSecond?.label}
            </Typography>
          </Toolbar>
        </AppBar>
        <Divider />
        <List sx={{ width: '100%' }}>
          {(selectedSecond?.subMenu || []).map((item, idx) => (
            <ListItemButton
              key={
                item.feature?.documentId ||
                (isMeetingRoomExtras(item.extras ?? null) &&
                typeof (item.extras as MeetingRoomExtras).internal_id === 'string'
                  ? (item.extras as MeetingRoomExtras).internal_id!
                  : item.code)
              }
              selected={idx === 0}
              onClick={() => handleThirdLevelClick(item, selectedSecond, null)}
            >
              <ListItemText primary={item.label} />
            </ListItemButton>
          ))}
        </List>
      </Stack>
    </Box>
  );

  const renderSolutionsMenu = () => (
    <Box p={2}>
      <Stack direction="row" alignItems="center" spacing={1} mb={1}>
        <AutoAwesomeIcon />
        <Typography variant="subtitle1">Soluções</Typography>
        <Divider flexItem sx={{ flex: 1, alignSelf: 'center' }} />
      </Stack>
      <Grid container spacing={2}>
        {solutionsSubMenus.map(item => (
          <Grid item xs={6} key={item.documentId || item.path}>
            <Paper
              elevation={2}
              sx={{
                p: 2,
                textAlign: 'center',
                cursor: 'pointer',
                bgcolor: 'background.default',
              }}
              onClick={() => {
                selectMenuByPath(item.path);
                navigate(item.path);
                setDrawerOpen(false);
              }}
            >
              <AutoAwesomeIcon sx={{ fontSize: 28 }} />
              <Typography variant="body2">{item.label || ''}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  // Atualiza o título do menu quando um item do marketplace é selecionado
  useEffect(() => {
    if (selectedMarketplaceItem) {
      const selectedItem = marketplaceItems.find(item => item.type === selectedMarketplaceItem);
      if (selectedItem) {
        updateMenuTitle(selectedItem.name);
      }
    }
  }, [selectedMarketplaceItem, marketplaceItems, updateMenuTitle]);

  return (
    <>
      <Box
        className="mobile-bottom-nav"
        sx={{
          position: 'fixed',
          left: 0,
          right: 0,
          zIndex: 1200,
          height: '7vh',
          p: 0.5,
          bgcolor: 'background.default',
        }}
      >
        <BottomNavigation
          value={selectedMenu?.documentId || navValue}
          onChange={(_, newValue) => {
            if (newValue === 'menu') {
              setDrawerOpen(true);
              setNavValue('menu');
              setSelectedSecond(null);
              setSelectedFirstForThird(null);
            } else if (newValue === 'solutions') {
              setDrawerOpen(true);
              setNavValue('solutions');
              setSelectedSecond(null);
              setSelectedFirstForThird(null);
            } else {
              const menuItem = menu?.find(item => item.documentId === newValue);
              if (menuItem) {
                handleMenuClick(menuItem);
              }
            }
          }}
          showLabels
          sx={{ height: '100%' }}
        >
          {/* Cockpit/Cockpit Agenda (se existir) */}
          {(cockpitAgendaMenu || cockpitMenu) && (
            <BottomNavigationAction
              label={(cockpitAgendaMenu || cockpitMenu)?.label || ''}
              value={(cockpitAgendaMenu || cockpitMenu)?.documentId}
              icon={renderMenuIcon(
                (cockpitAgendaMenu || cockpitMenu)?.icon,
                (cockpitAgendaMenu || cockpitMenu)?.label || ''
              )}
              sx={{
                borderTop: '4px solid',
                borderTopColor:
                  selectedMenu?.documentId === (cockpitAgendaMenu || cockpitMenu)?.documentId
                    ? 'primary.main'
                    : 'transparent',
                borderRadius: 0,
                minWidth: 0,
                flex: 1,
                '& .MuiBottomNavigationAction-label': {
                  color: 'text.primary',
                  fontSize: '1rem',
                },
              }}
            />
          )}
          {/* Botão Menu central */}
          <BottomNavigationAction
            label="Menu"
            value="menu"
            icon={<MenuIcon fontSize="large" sx={{ color: 'text.primary' }} />}
            sx={{
              borderTop: '4px solid',
              borderTopColor: navValue === 'menu' ? 'primary.main' : 'transparent',
              borderRadius: 0,
              minWidth: 0,
              flex: 1,
              '& .MuiBottomNavigationAction-label': {
                color: 'text.primary',
                fontSize: '1rem',
              },
            }}
          />
          {/* Botão Soluções (se existir) */}
          {/*   {solutionsMenu && (
            <BottomNavigationAction
              label={solutionsMenu.label}
              value="solutions"
              icon={<AutoAwesomeIcon fontSize="large" sx={{ color: 'text.primary' }} />}
              sx={{
                borderTop: '4px solid',
                borderTopColor: navValue === 'solutions' ? 'primary.main' : 'transparent',
                borderRadius: 0,
                minWidth: 0,
                flex: 1,
                '& .MuiBottomNavigationAction-label': {
                  color: 'text.primary',
                  fontSize: '1rem',
                },
              }}
            />
          )} */}
        </BottomNavigation>
      </Box>
      <Drawer
        anchor="bottom"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            minHeight: '30vh',
            maxHeight: '85vh',
            bgcolor: 'background.default',
          },
        }}
      >
        {!selectedSecond && navValue === 'menu' && renderMenuGroups()}
        {!selectedSecond && navValue === 'solutions' && renderSolutionsMenu()}
        {selectedSecond && renderThirdLevel()}
        <Box sx={{ p: 2, pb: 3 }}>
          <Button onClick={handleOpenLogoutModal} variant="minimal" fullWidth>
            Sair da plataforma
          </Button>
        </Box>

        <ConfirmLogoutModal
          open={logoutModalOpen}
          onClose={handleCloseLogoutModal}
          onConfirm={handleConfirmLogout}
        />
      </Drawer>
    </>
  );
};

export default MobileMenu;
